import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactReduxContext } from 'react-redux';
import { roleLabel } from '../../utils/roleLabel';
import { FiLogOut, FiChevronsLeft, FiChevronsRight, FiChevronDown } from 'react-icons/fi';
import { groupMenuItems } from './menuConfig';
import useMyBranches from '../../hooks/useMyBranches';
import { resolveActiveBranchDisplay } from '../../utils/activeBranchDisplay';

// Groups the user folded away (owner, 2026-10-09: "make the groups
// collapsible"). Kept per browser by heading key; storage can be blocked
// (private mode), so every read/write is guarded and failure = all open.
const COLLAPSED_KEY = 'mm.nav.collapsedGroups';
function readCollapsed() {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw : []);
  } catch (_) {
    return new Set();
  }
}
function writeCollapsed(set) {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...set]));
  } catch (_) {
    /* storage blocked: the fold just lasts this visit */
  }
}

/** A group heading that folds its group; larger than the old 10px caption. */
function GroupHeading({ label, open, onToggle, controls, count = 0 }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className="w-full flex items-center justify-between gap-2 px-3 pt-4 pb-1.5 rounded-lg text-[13px] font-semibold text-content-secondary hover:text-content transition-colors"
    >
      <span className="truncate flex-1 text-start">{label}</span>
      {/* A folded group still shows what is waiting inside it (unread
          WhatsApp, approvals), or folding Follow-up would hide both badges. */}
      {!open && count > 0 && (
        <span
          data-testid="nav-group-count"
          className="min-w-[18px] h-[18px] rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center px-1 tabular-nums shrink-0"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
      <FiChevronDown
        size={15}
        aria-hidden="true"
        className={`shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-90 rtl:rotate-90'}`}
      />
    </button>
  );
}

/**
 * Brand navigation — expandable sidebar on desktop (full labels when
 * expanded, icon rail when collapsed), full drawer on mobile. Uses semantic
 * tokens so it follows the active theme; teal active pill.
 */
const Sidebar = ({ user, menuItems, open, onClose, onLogout, expanded = true, onToggleExpand, whatsappUnreadCount = 0, approvalsCount = 0 }) => {
  // Count badges by destination: WhatsApp unread, approvals waiting on me.
  const badgeFor = (path) => {
    if (path === '/whatsapp') return [whatsappUnreadCount, 'wa-unread-badge'];
    if (path === '/approvals') return [approvalsCount, 'approvals-badge'];
    return [0, null];
  };
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // Peek-on-hover. `expanded` is the PINNED preference (persisted); this is the
  // temporary reveal while the pointer is over the rail, so a collapsed menu can
  // be read without committing to a click and losing the extra content width.
  //
  // It expands as an OVERLAY: Layout keeps its ms-20 margin, so nothing on the
  // page shifts under the pointer while the user is reading labels.
  const [peeking, setPeeking] = useState(false);
  const wide = expanded || peeking;
  const roleName = user?.role?.name;
  const isSuperAdmin = roleName === 'Super Admin';
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  const reduxContext = useContext(ReactReduxContext);
  let activeGarageId = reduxContext?.store?.getState()?.auth?.activeGarageId;
  if (!activeGarageId) {
    try {
      activeGarageId = localStorage.getItem('activeGarageId');
    } catch (_) {
      activeGarageId = null;
    }
  }
  const { branches, multiBranch } = useMyBranches();

  const { name: activeBranchName, logo: activeBranchLogo } = resolveActiveBranchDisplay({
    activeGarageId,
    user,
    branches,
    multiBranch,
    language: i18n.language,
    t,
  });

  const garageLogo = isSuperAdmin ? '/assets/logo-icon.png' : activeBranchLogo;
  const orgName = isSuperAdmin ? 'MotorMind' : activeBranchName;

  // Two entries can both prefix-match one URL — /hr/reports and
  // /hr/reports/profitability — which lit up two rows at once. The page you
  // are actually on is the LONGEST match, so resolve that once and compare
  // against it instead of testing each item in isolation.
  const activePath = (menuItems || []).reduce((best, item) => {
    const matches =
      location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(`${item.path}/`));
    if (!matches) return best;
    return item.path.length > best.length ? item.path : best;
  }, '');

  const isActive = (item) => item.path === activePath;

  // Group the (already permission-filtered) flat menu into ordered sections.
  const sections = groupMenuItems(menuItems);

  // Arriving on a page whose group is folded (a link, the palette, a bell)
  // unfolds that group, so the current page is never hidden from the menu.
  // Done in the initializer too, so a folded group never flashes on load.
  const activeGroup = sections.find((sec) => sec.items.some((i) => i.path === activePath))?.heading;
  const [collapsed, setCollapsed] = useState(() => {
    const initial = readCollapsed();
    if (activeGroup) initial.delete(activeGroup);
    return initial;
  });
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeCollapsed(next);
      return next;
    });
  // Keyed on the page, not just its group: fold the group you are on, then
  // open another page in it, and it must unfold again.
  useEffect(() => {
    if (!activeGroup) return;
    setCollapsed((prev) => {
      if (!prev.has(activeGroup)) return prev;
      const next = new Set(prev);
      next.delete(activeGroup);
      writeCollapsed(next);
      return next;
    });
  }, [activePath, activeGroup]);
  const groupCount = (sec) => sec.items.reduce((sum, item) => sum + (badgeFor(item.path)[0] || 0), 0);
  // A folded group shows only its heading. The icon rail has no headings, so
  // it always lists every icon.
  const shownItems = (sec, withHeadings = true) =>
    withHeadings && sec.heading && collapsed.has(sec.heading) ? [] : sec.items;

  const logo = (
    <div
      className="w-9 h-9 rounded-lg bg-white ring-1 ring-edge p-1 shrink-0 flex items-center justify-center overflow-hidden"
      title={orgName || 'MotorMind'}
    >
      <img
        src={isSuperAdmin ? '/assets/logo-icon.png' : garageLogo}
        alt={orgName || 'MotorMind'}
        className="w-full h-full object-contain"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = '/assets/logo-icon.png';
        }}
      />
    </div>
  );

  return (
    <>
      {/* Desktop — expandable sidebar */}
      <aside
        data-testid="desktop-sidebar"
        // Focus-within as well as hover: tabbing into a collapsed rail must
        // reveal the labels too, or the menu is unusable without a mouse.
        onMouseEnter={() => !expanded && setPeeking(true)}
        onMouseLeave={() => setPeeking(false)}
        onFocus={() => !expanded && setPeeking(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPeeking(false);
        }}
        className={`hidden xl:flex fixed inset-y-0 start-0 z-50 bg-surface border-e border-edge flex-col
          transition-[width] duration-200 ease-in-out ${wide ? 'w-64' : 'w-20'} ${
            peeking && !expanded ? 'shadow-2xl' : ''
          }`}
      >
        <div
          className={`flex items-center h-16 shrink-0 border-b border-edge ${
            wide ? 'gap-3 px-4' : 'justify-center px-2'
          }`}
        >
          {logo}
          {wide && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-content truncate leading-tight">{orgName}</p>
              {isSuperAdmin && (
                <p className="text-[11px] text-content-muted leading-tight mt-0.5">{t('sa.superAdmin')}</p>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-hide py-3 flex flex-col gap-0.5 px-2.5">
          {sections.map((sec, si) => (
            <div key={sec.key} id={`nav-grp-d-${sec.key}`} className="flex flex-col gap-0.5">
              {sec.heading && (
                wide ? (
                  <GroupHeading
                    label={t(sec.heading)}
                    open={!collapsed.has(sec.heading)}
                    onToggle={() => toggleGroup(sec.heading)}
                    controls={`nav-grp-d-${sec.key}`}
                    count={groupCount(sec)}
                  />
                ) : (
                  si > 0 && <div className="mx-2 my-1.5 border-t border-edge" aria-hidden="true" />
                )
              )}
              {shownItems(sec, wide).map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                const [badgeCount, badgeId] = badgeFor(item.path);
                const showBadge = badgeCount > 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={active ? 'page' : undefined}
                    title={t(item.i18nKey)}
                    className={
                      wide
                        ? `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                            active
                              ? 'bg-accent-soft text-accent font-medium'
                              : 'text-content-secondary hover:text-content hover:bg-surface-2'
                          }`
                        // Icons only, centred, one comfortable tap target each.
                        // The rail used to print a 9px label under every icon,
                        // which at 80px wide truncated most of them to noise
                        // ("عروض الأسعا…", "تحصيلات بواب…") — unreadable, and it
                        // made the rail look broken rather than compact. The
                        // label is one hover away now, at full size.
                        : `group relative flex items-center justify-center h-11 rounded-xl transition-colors ${
                            active
                              ? 'bg-accent-soft text-accent'
                              : 'text-content-muted hover:text-content hover:bg-surface-2'
                          }`
                    }
                  >
                    {wide && active && (
                      <span className="absolute inset-y-2 start-0 w-0.5 rounded-full bg-accent" />
                    )}
                    <span className="relative">
                      <Icon size={wide ? 18 : 20} className="shrink-0" />
                      {!wide && showBadge && (
                        <span
                          data-testid={badgeId}
                          className="absolute -top-1 -end-1 min-w-[14px] h-[14px] rounded-full bg-accent text-white text-[9px] font-bold flex items-center justify-center px-0.5 tabular-nums"
                        >
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </span>
                    {wide && (
                      <>
                        <span className="truncate flex-1">{t(item.i18nKey)}</span>
                        {showBadge && (
                          <span
                            data-testid={badgeId}
                            className="min-w-[18px] h-[18px] rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center px-1 tabular-nums shrink-0"
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="shrink-0 py-3 px-2.5 border-t border-edge space-y-0.5">
          {/* The pin. `expanded` is a stored preference, not a view state: pinned
            keeps the menu open and pushes the page over; unpinned leaves a rail
            that reveals itself on hover. Labelled as pin/unpin rather than
            expand/collapse, because with peek-on-hover "expand" no longer
            describes what the button changes. */}
          <button
            onClick={onToggleExpand}
            title={expanded ? t('nav.unpinMenu') : t('nav.pinMenu')}
            aria-pressed={expanded}
            className={`w-full flex items-center rounded-lg text-content-muted hover:text-content hover:bg-surface-2 transition-colors ${
              wide ? 'gap-3 px-3 py-2.5 text-sm' : 'justify-center h-11'
            }`}
          >
            {expanded ? (
              <FiChevronsLeft size={18} className="rtl:-scale-x-100 shrink-0" />
            ) : (
              <FiChevronsRight size={20} className="rtl:-scale-x-100 shrink-0" />
            )}
            {wide && (
              <span className="truncate">{expanded ? t('nav.unpinMenu') : t('nav.pinMenu')}</span>
            )}
          </button>
          <button
            onClick={onLogout}
            title={t('logout')}
            className={`w-full flex items-center rounded-lg text-content-secondary hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors ${
              wide ? 'gap-3 px-3 py-2.5 text-sm' : 'justify-center h-11'
            }`}
          >
            <FiLogOut size={wide ? 18 : 20} className="rtl:-scale-x-100 shrink-0" />
            {wide && <span className="truncate">{t('logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile / tablet drawer — pad the top by the device safe-area inset so
          the header (logo + names) clears the status bar / notch instead of
          hiding behind it. */}
      <aside
        className={`xl:hidden fixed inset-y-0 start-0 z-50 w-72 bg-surface border-e border-edge
          pt-[env(safe-area-inset-top)]
          transform transition-transform duration-300 ease-in-out flex flex-col ${
            open ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
          }`}
      >
        <div className="flex items-center gap-3 px-4 h-16 border-b border-edge shrink-0">
          {logo}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-content truncate leading-tight">{orgName}</p>
            {isSuperAdmin && (
              <p className="text-[11px] text-content-muted leading-tight mt-0.5">{t('sa.superAdmin')}</p>
            )}
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-hide">
          {sections.map((sec) => (
            <div key={sec.key} id={`nav-grp-m-${sec.key}`} className="space-y-0.5">
              {sec.heading && (
                <GroupHeading
                  label={t(sec.heading)}
                  open={!collapsed.has(sec.heading)}
                  onToggle={() => toggleGroup(sec.heading)}
                  controls={`nav-grp-m-${sec.key}`}
                  count={groupCount(sec)}
                />
              )}
              {shownItems(sec).map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                const [badgeCount, badgeId] = badgeFor(item.path);
                const showBadge = badgeCount > 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={active ? 'page' : undefined}
                    onClick={onClose}
                    className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      active
                        ? 'bg-accent-soft text-accent font-medium'
                        : 'text-content-secondary hover:text-content hover:bg-surface-2'
                    }`}
                  >
                    {active && <span className="absolute inset-y-2 start-0 w-0.5 rounded-full bg-accent" />}
                    <Icon size={18} className="shrink-0" />
                    <span className="truncate flex-1">{t(item.i18nKey)}</span>
                    {showBadge && (
                      <span
                        data-testid={badgeId}
                        className="min-w-[18px] h-[18px] rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center px-1 tabular-nums shrink-0"
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-edge shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          {/* Signed-in user */}
          {userName && (
            <div className="flex items-center gap-3 px-3 py-2 mb-1">
              <div className="w-9 h-9 rounded-full bg-content text-content-inverse flex items-center justify-center font-semibold text-xs shrink-0">
                {userInitials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-content truncate leading-tight">{userName}</p>
                {roleName && (
                  <p className="text-[11px] text-content-muted truncate leading-tight mt-0.5">{roleLabel(user?.role)}</p>
                )}
              </div>
            </div>
          )}
          <button
            onClick={onLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
          >
            <FiLogOut size={18} className="rtl:-scale-x-100" />
            <span>{t('logout')}</span>
          </button>
          {!isSuperAdmin && (
            <p className="px-3 pt-2 text-[10px] text-content-muted tracking-wide">
              {t('cm.sidebar.poweredBy')} <span className="text-content-secondary font-medium">MotorMind</span>
            </p>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
